import React, { Component } from 'react';
import { Col, Row } from 'reactstrap';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import format from 'date-fns/format';

// Components
import TimeSlot from 'components/atoms/TimeSlot';
import ContainerTruncate from 'components/atoms/ContainerTruncate/';
import { ns as truncateClassName } from 'components/atoms/ContainerTruncate/ContainerTruncate';
import { ns as dataSlotClassName } from 'components/atoms/DataSlot/DataSlot';
import LoaderHOC from 'components/atoms/LoaderHOC';

// Utils
import { isMobile, isStaticHosted, isInsideIFrame } from 'utils/DOM';
import { formatIOSDate } from 'utils/date';

// Enums
import { APP_SIZES } from 'constants/appConfig';

import TimeSelectorLoader from './TimeSelectorLoader';
import './TimeSelector.scss';
import isWithinRange from 'date-fns/is_within_range';

const ns = 'time-selector-container';
const { MAX_HEIGHT_TIME_SELECTOR } = APP_SIZES;

class TimeSelector extends Component {
  componentDidUpdate() {
    const { scrollDown } = this.props;
    const isFramedAndIsStaticHosting = isInsideIFrame() && isStaticHosted();
    if (scrollDown && isMobile() && !isFramedAndIsStaticHosting) {
      setTimeout(() => {
        window.scroll({
          top: document.body.scrollHeight,
          behavior: 'smooth'
        });
      }, 0);
    }
  }

  /**
   * Adds tab indexes to the visible data slots only
   */
  addTabIndexation = () => {
    const truncateContainers = document.querySelectorAll('[hours-range]');
    const { filteredTimeslots } = this.props;
    if (!isMobile() && truncateContainers.length > 0) {
      truncateContainers.forEach(truncateContainer => {
        const dataSlots = truncateContainer.getElementsByClassName(
          dataSlotClassName
        );
        const overflowingContainer = truncateContainer.getElementsByClassName(
          truncateClassName
        )[0];
        let count;

        if (overflowingContainer) {
          count = this.getCountOfHoursDisplayed(
            overflowingContainer,
            dataSlots[0]
          );
        } else {
          count =
            filteredTimeslots[truncateContainer.getAttribute('hours-range')]
              .length;
        }
        const indexes = Object.keys(dataSlots);
        indexes.slice(count, indexes.length).forEach(index => {
          dataSlots[index].removeAttribute('tabIndex');
        });
      });
    }
  };

  /**
   * Calculates how many slots are being rendered without being hidden, overflowing
   * @param overflowingContainer
   * @param dataSlot
   * @returns {number}
   */
  getCountOfHoursDisplayed = (overflowingContainer, dataSlot) => {
    const toPixels = pixels => {
      return parseInt(pixels.replace('px', ''), 10);
    };
    const height = overflowingContainer.clientHeight;
    const width = overflowingContainer.clientWidth;
    const currentSlotStyle =
      dataSlot.currentStyle || window.getComputedStyle(dataSlot);
    const unitHeight =
      dataSlot.offsetHeight +
      toPixels(currentSlotStyle.marginTop) +
      toPixels(currentSlotStyle.marginBottom);
    const unitWidth =
      dataSlot.offsetWidth +
      toPixels(currentSlotStyle.marginLeft) +
      toPixels(currentSlotStyle.marginRight);

    return Math.round(height / unitHeight) * Math.round(width / unitWidth);
  };

  selectOption(teamIds, time, promotionInRange) {
    const {
      setTeamIds,
      setStartTime,
      setSelectedTimeslotInPromoRange
    } = this.props;
    setTeamIds(teamIds);
    setStartTime(time);
    setSelectedTimeslotInPromoRange(promotionInRange);
  }

  isSelectedDate(time) {
    const { startTime } = this.props;
    if (!startTime) {
      return false;
    }
    return startTime.getTime() === time.getTime();
  }

  drawSection(title, content, hoursRange) {
    const {
      selectedDate,
      promoStartTime,
      promoEndTime,
      promotionStartDate,
      promotionEndDate,
      daysOfWeek,
      setTeamIds,
      intl
    } = this.props;
    const date = selectedDate ? format(selectedDate, 'YYYY-MM-DD') : '';
    const startTime = formatIOSDate(`${date}T${promoStartTime}`);
    const endTime = formatIOSDate(`${date}T${promoEndTime}`);
    const weekDay = selectedDate && +format(selectedDate, 'd');
    const promotionToday = daysOfWeek ? Boolean(+daysOfWeek[weekDay]) : false;
    const applyPromotion =
      isWithinRange(date, promotionStartDate, promotionEndDate) &&
      promotionToday;

    if (content.length)
      return (
        <div hours-range={hoursRange}>
          <div className="time-selector__label">
            <div
              className={`${ns}--fit`}
              tabIndex={0}
              aria-label={intl.formatMessage({
                id: 'c8.aria.hours.range',
                defaultMessage: `Available ${title} hours`
              })}
            >
              {title}
            </div>
          </div>
          <ContainerTruncate
            key={title + this.props.selectedDate}
            maxContentHeight={MAX_HEIGHT_TIME_SELECTOR}
            onComponentUpdate={this.addTabIndexation}
            ariaLabelConfig={intl.formatMessage({
              id: 'c8.aria.show.more.hours',
              defaultMessage: `Show more hours`
            })}
          >
            <div className="time-selector__group">
              {content.map(obj => {
                const { time, teamIds } = obj;
                const promotionInRange = isWithinRange(
                  time,
                  startTime,
                  endTime
                );
                if (this.isSelectedDate(time)) {
                  setTeamIds(teamIds);
                }
                return (
                  <TimeSlot
                    key={obj.time.getTime()}
                    bsStyle="primary"
                    onClick={() =>
                      this.selectOption(obj.teamIds, obj.time, promotionInRange)
                    }
                    active={this.isSelectedDate(obj.time)}
                    date={obj.time}
                    hasPromotion={applyPromotion && promotionInRange}
                    amPmAriaLabel={title}
                  />
                );
              })}
            </div>
          </ContainerTruncate>
        </div>
      );
  }

  render() {
    const {
      className,
      filteredTimeslots: { morning, afternoon },
      intl
    } = this.props;
    const classProps = classNames(ns, className);
    const am = intl.formatMessage({
      id: 'c8.appointment.page.time.selector.am'
    });
    const pm = intl.formatMessage({
      id: 'c8.appointment.page.time.selector.pm'
    });

    return (
      <LoaderHOC
        serviceId={'firstAvailable'}
        placeHolderType={() => <TimeSelectorLoader />}
        component={() => (
          <Row>
            <Col className={classProps}>
              <div
                className="time-selector"
                id="time_selector"
                tabIndex={0}
                aria-label={intl.formatMessage({
                  id: 'c8.aria.select.time.of.appointment',
                  defaultMessage: 'Select time of appointment'
                })}
              >
                <div className={`${ns}--fit`}>
                  {this.drawSection(am, morning, 'morning')}
                  {this.drawSection(pm, afternoon, 'afternoon')}
                </div>
              </div>
            </Col>
          </Row>
        )}
      />
    );
  }
}

TimeSelector.propTypes = {
  className: PropTypes.string,
  daysOfWeek: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  filteredTimeslots: PropTypes.object,
  intl: PropTypes.object.isRequired,
  promoEndTime: PropTypes.string,
  promoStartTime: PropTypes.string,
  promotionEndDate: PropTypes.string,
  promotionStartDate: PropTypes.string,
  scrollDown: PropTypes.bool,
  selectedDate: PropTypes.object,
  setStartTime: PropTypes.func.isRequired,
  setTeamIds: PropTypes.func.isRequired,
  setSelectedTimeslotInPromoRange: PropTypes.func.isRequired,
  startTime: PropTypes.instanceOf(Date)
};

TimeSelector.defaultProps = {
  className: ''
};

export default TimeSelector;



// WEBPACK FOOTER //
// ./src/components/molecules/TimeSelector/TimeSelector.js